import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { LineChart, Line, BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, CartesianGrid, Legend } from 'recharts';
import { TrendingUp, TrendingDown, ArrowLeft, Loader2, AlertTriangle } from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';
import { Link } from 'react-router-dom';
import { Button } from '../components/ui/button';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const fadeUp = { hidden: { opacity: 0, y: 20 }, visible: { opacity: 1, y: 0 } };
const stagger = { visible: { transition: { staggerChildren: 0.1 } } };

export default function CompareReviews() {
  const { user } = useAuth();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (user && user.email) {
      axios.get(`${API}/analytics/compare`, { withCredentials: true })
        .then(res => setData(res.data))
        .catch(() => {})
        .finally(() => setLoading(false));
    }
  }, [user]);

  if (loading) return <div className="min-h-screen pt-20 flex items-center justify-center"><Loader2 className="w-8 h-8 animate-spin text-teal-600" /></div>;
  if (!data || data.reviews.length === 0) {
    return (
      <div className="min-h-screen pt-20 bg-slate-50">
        <div className="max-w-7xl mx-auto px-6 md:px-12 lg:px-24 py-16 text-center">
          <AlertTriangle className="w-12 h-12 text-slate-300 mx-auto mb-4" />
          <h2 className="font-heading text-2xl font-medium text-slate-700 mb-2">No Reviews to Compare</h2>
          <p className="text-slate-500 mb-6">Launch at least two reviews to see trend comparisons.</p>
          <Link to="/dashboard"><Button className="bg-teal-600 hover:bg-teal-700 text-white">Back to Dashboard</Button></Link>
        </div>
      </div>
    );
  }

  const reviewsWithData = data.reviews.filter(r => r.total_responses > 0);

  // Trend line data
  const trendData = reviewsWithData.map((r, i) => ({
    name: r.title.length > 16 ? r.title.slice(0, 16) + '...' : r.title,
    date: new Date(r.date).toLocaleDateString('en-US', { month: 'short', day: 'numeric' }),
    wellness: r.overall_wellness,
    highRisk: r.high_risk_pct,
    responses: r.total_responses,
    wis: r.avg_wis,
    ees: r.avg_ees,
  }));

  // Side-by-side comparison (latest 2)
  const latest2 = reviewsWithData.slice(-2);
  const hasTwoReviews = latest2.length === 2;
  const wellnessDelta = hasTwoReviews ? (latest2[1].overall_wellness - latest2[0].overall_wellness).toFixed(1) : null;
  const riskDelta = hasTwoReviews ? (latest2[1].high_risk_pct - latest2[0].high_risk_pct).toFixed(1) : null;

  // Department comparison across reviews (aggregate all depts from all reviews)
  const deptSet = new Set();
  reviewsWithData.forEach(r => r.departments.forEach(d => deptSet.add(d.name)));
  const deptCompare = Array.from(deptSet).map(deptName => {
    const entry = { department: deptName };
    reviewsWithData.forEach((r, i) => {
      const d = r.departments.find(x => x.name === deptName);
      entry[`review_${i}`] = d ? d.wellness_score : null;
    });
    return entry;
  });

  return (
    <div className="min-h-screen pt-20 bg-slate-50" data-testid="compare-reviews-page">
      <div className="max-w-7xl mx-auto px-6 md:px-12 lg:px-24 py-8">
        {/* Header */}
        <motion.div initial="hidden" animate="visible" variants={stagger} className="mb-8">
          <motion.div variants={fadeUp} className="flex items-center gap-3 mb-4">
            <Link to="/dashboard" className="text-slate-400 hover:text-slate-600"><ArrowLeft className="w-5 h-5" /></Link>
            <h1 className="font-heading text-3xl font-semibold text-slate-800">Review Trends</h1>
          </motion.div>
          <motion.p variants={fadeUp} className="text-sm text-slate-500">Track workforce wellness across multiple Resilience Reviews</motion.p>
        </motion.div>

        {/* Side-by-Side Comparison */}
        {hasTwoReviews && (
          <motion.div initial="hidden" animate="visible" variants={stagger} className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-8">
            <motion.div variants={fadeUp} className="glass-card rounded-2xl p-6 text-center">
              <p className="text-xs font-semibold text-slate-500 uppercase tracking-wider mb-2">Wellness Score</p>
              <div className="flex items-center justify-center gap-4">
                <span className="font-heading text-2xl font-semibold text-slate-400">{latest2[0].overall_wellness}%</span>
                <span className="text-slate-300">&rarr;</span>
                <span className="font-heading text-2xl font-semibold text-slate-800">{latest2[1].overall_wellness}%</span>
              </div>
              <div className={`flex items-center justify-center gap-1 mt-2 text-sm font-medium ${parseFloat(wellnessDelta) >= 0 ? 'text-green-600' : 'text-red-500'}`}>
                {parseFloat(wellnessDelta) >= 0 ? <TrendingUp className="w-4 h-4" /> : <TrendingDown className="w-4 h-4" />}
                {wellnessDelta > 0 ? '+' : ''}{wellnessDelta}%
              </div>
            </motion.div>
            <motion.div variants={fadeUp} className="glass-card rounded-2xl p-6 text-center">
              <p className="text-xs font-semibold text-slate-500 uppercase tracking-wider mb-2">High Risk %</p>
              <div className="flex items-center justify-center gap-4">
                <span className="font-heading text-2xl font-semibold text-slate-400">{latest2[0].high_risk_pct}%</span>
                <span className="text-slate-300">&rarr;</span>
                <span className="font-heading text-2xl font-semibold text-slate-800">{latest2[1].high_risk_pct}%</span>
              </div>
              <div className={`flex items-center justify-center gap-1 mt-2 text-sm font-medium ${parseFloat(riskDelta) <= 0 ? 'text-green-600' : 'text-red-500'}`}>
                {parseFloat(riskDelta) <= 0 ? <TrendingDown className="w-4 h-4" /> : <TrendingUp className="w-4 h-4" />}
                {riskDelta > 0 ? '+' : ''}{riskDelta}%
              </div>
            </motion.div>
            <motion.div variants={fadeUp} className="glass-card rounded-2xl p-6 text-center">
              <p className="text-xs font-semibold text-slate-500 uppercase tracking-wider mb-2">Avg Culture Friction</p>
              <div className="flex items-center justify-center gap-4">
                <span className="font-heading text-2xl font-semibold text-slate-400">{latest2[0].avg_wis}</span>
                <span className="text-slate-300">&rarr;</span>
                <span className="font-heading text-2xl font-semibold text-slate-800">{latest2[1].avg_wis}</span>
              </div>
              <p className="text-xs text-slate-400 mt-2">WIS Total (lower is better)</p>
            </motion.div>
          </motion.div>
        )}

        {/* Wellness Trend Line Chart */}
        {trendData.length > 0 && (
          <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} className="glass-card rounded-2xl p-6 mb-8" data-testid="wellness-trend-chart">
            <h3 className="font-heading text-lg font-medium text-slate-800 mb-6">Wellness Score Trend</h3>
            <ResponsiveContainer width="100%" height={300}>
              <LineChart data={trendData}>
                <CartesianGrid strokeDasharray="3 3" stroke="#E2E8F0" />
                <XAxis dataKey="date" tick={{ fontSize: 12, fill: '#64748B' }} />
                <YAxis domain={[0, 100]} tick={{ fontSize: 12, fill: '#94A3B8' }} />
                <Tooltip contentStyle={{ background: '#1E293B', border: 'none', borderRadius: '12px', color: '#fff', fontSize: 13 }} />
                <Legend />
                <Line type="monotone" dataKey="wellness" name="Wellness %" stroke="#0D9488" strokeWidth={3} dot={{ r: 5, fill: '#0D9488' }} />
                <Line type="monotone" dataKey="highRisk" name="High Risk %" stroke="#EF4444" strokeWidth={2} dot={{ r: 4, fill: '#EF4444' }} strokeDasharray="5 5" />
              </LineChart>
            </ResponsiveContainer>
          </motion.div>
        )}

        {/* Burnout Metrics Bar Comparison */}
        {trendData.length > 0 && (
          <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.1 }} className="glass-card rounded-2xl p-6 mb-8" data-testid="burnout-metrics-chart">
            <h3 className="font-heading text-lg font-medium text-slate-800 mb-6">Burnout Metrics per Review</h3>
            <ResponsiveContainer width="100%" height={280}>
              <BarChart data={trendData}>
                <CartesianGrid strokeDasharray="3 3" stroke="#E2E8F0" />
                <XAxis dataKey="date" tick={{ fontSize: 12, fill: '#64748B' }} />
                <YAxis tick={{ fontSize: 12, fill: '#94A3B8' }} />
                <Tooltip contentStyle={{ background: '#1E293B', border: 'none', borderRadius: '12px', color: '#fff', fontSize: 13 }} />
                <Legend />
                <Bar dataKey="wis" name="Avg Culture Friction" fill="#0D9488" radius={[6, 6, 0, 0]} barSize={28} />
                <Bar dataKey="ees" name="Avg Emotional Fatigue" fill="#F59E0B" radius={[6, 6, 0, 0]} barSize={28} />
              </BarChart>
            </ResponsiveContainer>
          </motion.div>
        )}

        {/* Department Wellness Table */}
        {deptCompare.length > 0 && reviewsWithData.length > 1 && (
          <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.2 }} className="glass-card rounded-2xl p-6" data-testid="dept-comparison-table">
            <h3 className="font-heading text-lg font-medium text-slate-800 mb-4">Department Wellness Across Reviews</h3>
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead>
                  <tr className="border-b border-slate-100">
                    <th className="text-left text-xs font-semibold text-slate-500 uppercase tracking-wider px-4 py-3">Department</th>
                    {reviewsWithData.map((r, i) => (
                      <th key={i} className="text-center text-xs font-semibold text-slate-500 uppercase tracking-wider px-4 py-3">
                        {new Date(r.date).toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {deptCompare.map((row, idx) => (
                    <tr key={idx} className="border-b border-slate-50">
                      <td className="px-4 py-3 text-sm font-medium text-slate-700">{row.department}</td>
                      {reviewsWithData.map((_, i) => {
                        const val = row[`review_${i}`];
                        return (
                          <td key={i} className="px-4 py-3 text-center">
                            {val !== null ? (
                              <span className={`text-sm font-semibold ${val >= 70 ? 'text-green-600' : val >= 50 ? 'text-amber-600' : 'text-red-500'}`}>
                                {val}%
                              </span>
                            ) : (
                              <span className="text-xs text-slate-300">-</span>
                            )}
                          </td>
                        );
                      })}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </motion.div>
        )}
      </div>
    </div>
  );
}
